import {
    AccountInfo,
    createNestablePublicClientApplication,
    IPublicClientApplication,
} from '@azure/msal-browser';
import { nestedAppAuth } from '@microsoft/teams-js';
import { loginRequest, msalConfig } from '@/lib/authConfig';

let nestedClient: Promise<IPublicClientApplication> | null = null;

/** Rollout switch: NAA is tried only when the build sets `VITE_TEAMS_NAA_ENABLED=true`. */
export function isTeamsNaaEnabled(): boolean {
    return import.meta.env.VITE_TEAMS_NAA_ENABLED === 'true';
}

function getNestedClient(): Promise<IPublicClientApplication> {
    if (nestedClient === null) {
        nestedClient = createNestablePublicClientApplication({
            ...msalConfig,
            auth: { ...msalConfig.auth, supportsNestedAppAuth: true },
        }).catch((error: unknown) => {
            // A failed creation must not stick: the next attempt builds the client again.
            nestedClient = null;
            throw error;
        });
    }
    return nestedClient;
}

function findCachedAccount(client: IPublicClientApplication, loginHint: string): AccountInfo | undefined {
    const wanted = loginHint.trim().toLowerCase();
    return client.getAllAccounts().find((account) => account.username.toLowerCase() === wanted);
}

/**
 * The user's id token through Nested App Authentication: the Teams host brokers it, so it needs neither
 * the browser's Microsoft cookies (blocked for a tab's iframe by third-party cookie blocking) nor a
 * `webApplicationInfo` matching this host. Null when NAA is off, the host does not recommend it, or the
 * silent request fails: the caller moves on to MSAL's cookie-based silent sign-in.
 */
export async function acquireTeamsNaaIdToken(loginHint: string): Promise<string | null> {
    if (!isTeamsNaaEnabled()) return null;

    try {
        if (!nestedAppAuth.isNAAChannelRecommended()) return null;

        const client = await getNestedClient();
        const account = findCachedAccount(client, loginHint);
        // NAA serves a cached id token without checking its expiry, so force a refresh. Logins are rare
        // (page load, pre-expiry refresh), which makes the extra bridge call cheap.
        // `forceRefresh` is honored by the nested controller's ssoSilent but missing from its request type,
        // hence the request is built as a variable (no excess property check).
        const ssoRequest = { ...loginRequest, loginHint, forceRefresh: true };
        const result = account
            ? await client.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true })
            : await client.ssoSilent(ssoRequest);

        return result.idToken?.trim() || null;
    } catch (error) {
        console.warn('Teams NAA unavailable, falling back to MSAL silent sign-in:', error);
        return null;
    }
}
